import React from 'react';
import { 
  Box, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField, 
  Button,
  SelectChangeEvent
} from '@mui/material';

interface SearchOption {
  value: string;
  label: string;
}

interface Column {
  id: string;
  label: string;
  searchable?: boolean;
  inputType?: 'text' | 'select';
  options?: SearchOption[];
}

interface SearchSectionProps {
  columns: Column[];
  searchField: string;
  searchValue: string;
  setSearchField: (value: string) => void;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  handleReset: () => void;
}

const SearchSection: React.FC<SearchSectionProps> = ({
  columns,
  searchField,
  searchValue,
  setSearchField,
  setSearchValue,
  handleSearch,
  handleReset
}) => {
  const searchableColumns = columns.filter(col => col.searchable !== false);
  
  // Find the current column configuration
  const currentColumn = searchableColumns.find(col => col.id === searchField);
  const inputType = currentColumn?.inputType || 'text';
  const options = currentColumn?.options || [];
  
  const handleFieldChange = (e: SelectChangeEvent) => {
    setSearchField(e.target.value);
    // Reset search value when changing fields
    setSearchValue('');
  };

  return (
    <Box sx={{ mb: 2, mt: 1, display: "flex", gap: 2, alignItems: "center" }}>
      <FormControl sx={{ minWidth: 200 }}>
        <InputLabel>Search Field</InputLabel>
        <Select
          value={searchField}
          label="Search Field"
          onChange={handleFieldChange}
          size="small"
        >
          {searchableColumns.map((column) => (
            <MenuItem key={column.id} value={column.id}>
              {column.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {inputType === 'select' && options.length > 0 ? (
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Search Value</InputLabel>
          <Select
            value={searchValue}
            label="Search Value"
            onChange={(e) => setSearchValue(e.target.value)}
            size="small"
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <TextField
          label="Search Value"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          autoFocus
        />
      )}

      <Button
        variant="contained"
        onClick={handleSearch}
        disabled={!searchField || !searchValue}
        size="small"
      >
        Search
      </Button>

      <Button
        variant="outlined"
        onClick={handleReset}
        size="small"
      >
        Reset
      </Button>
    </Box>
  );
};

export default SearchSection;